/**
 * CreateNewContext - Unified context for Create New modals
 * 
 * Provides:
 * - openCreateModal(modalKey, context?) - Open a modal with optional context
 * - closeCreateModal() - Close the active modal
 * - useCreateNew() hook for consuming components
 * 
 * HARDENING (P1.1):
 * - Modal keys must use MODAL_KEYS enum (validated at runtime)
 * - Context shape normalized: accountId is source of truth
 */
import React, { createContext, useContext, useState, useCallback } from 'react';

// Modal keys enum for type safety - ALL callers MUST use these
export const MODAL_KEYS = Object.freeze({
  ORDER: 'order',
  CUSTOMER: 'customer',
  CONTACT: 'contact',
  VENDOR: 'vendor',
  PRODUCT: 'product',
  PURCHASE_ORDER: 'purchase_order',
  BILL: 'bill',
  PAYMENT: 'payment',
});

// Valid modal keys set for runtime validation
const VALID_MODAL_KEYS = new Set(Object.values(MODAL_KEYS));

/**
 * Normalize context for Create Order modal
 * - accountId is the source of truth
 * - Display fields (name, email, phone) are for UI only
 * - prefillFromOrderIds is for reorder/combine feature
 * @param {object} context - Raw context from caller
 * @returns {object} Normalized context
 */
export const normalizeOrderContext = (context) => {
  if (!context) return null;
  
  // Extract accountId (support multiple field names)
  const accountId = context.accountId || context.account_id || context.id || null;
  
  // Extract prefillFromOrderIds for reorder/combine feature
  const prefillFromOrderIds = context.prefillFromOrderIds || context.orderIds || null;
  
  // If no accountId and no prefillFromOrderIds, context might be invalid
  if (!accountId && !prefillFromOrderIds) {
    console.warn('CreateNewContext: Order context provided without accountId or prefillFromOrderIds');
  }
  
  return {
    // Source of truth - used for API submission
    accountId,
    // Prefill from existing orders (reorder/combine feature)
    prefillFromOrderIds,
    // Display only - for UX, NOT used in API submission
    _display: {
      customerName: context.customerName || context.name || context.company_name || '',
      email: context.email || '',
      phone: context.phone || '',
    },
  };
};

// Context shape
const CreateNewContext = createContext({
  activeModalKey: null,
  modalContext: null,
  openCreateModal: () => {},
  closeCreateModal: () => {},
});

/**
 * CreateNewProvider - Wrap admin dashboard with this provider
 */
export const CreateNewProvider = ({ children }) => {
  const [activeModalKey, setActiveModalKey] = useState(null);
  const [modalContext, setModalContext] = useState(null);

  /**
   * Open a create modal
   * @param {string} modalKey - MUST be one of MODAL_KEYS (validated)
   * @param {object} context - Optional context (normalized for ORDER modal)
   */
  const openCreateModal = useCallback((modalKey, context = null) => {
    // HARDENING: Validate modal key at runtime
    if (!VALID_MODAL_KEYS.has(modalKey)) {
      console.error(`CreateNewContext: Invalid modalKey "${modalKey}". Use MODAL_KEYS enum.`);
      return;
    }
    
    // Normalize context for ORDER modal
    const normalizedContext = modalKey === MODAL_KEYS.ORDER 
      ? normalizeOrderContext(context)
      : context;
    
    setModalContext(normalizedContext);
    setActiveModalKey(modalKey);
  }, []);

  /**
   * Close the active modal and clear context
   */
  const closeCreateModal = useCallback(() => {
    setActiveModalKey(null);
    setModalContext(null);
  }, []);

  const value = {
    activeModalKey,
    modalContext,
    openCreateModal,
    closeCreateModal,
  };

  return (
    <CreateNewContext.Provider value={value}>
      {children}
    </CreateNewContext.Provider>
  );
};

/**
 * useCreateNew - Hook to access Create New context
 * @returns {{ activeModalKey, modalContext, openCreateModal, closeCreateModal }}
 */
export const useCreateNew = () => {
  const context = useContext(CreateNewContext);
  if (!context) {
    throw new Error('useCreateNew must be used within CreateNewProvider');
  }
  return context;
};

export default CreateNewContext;
